import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  LayoutDashboard,
  Calendar,
  Globe,
  User,
  Info,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Plus,
  Settings,
  Shield,
  FlaskConical,
  Briefcase,
  LifeBuoy
} from 'lucide-react';

const Sidebar = ({ isOpen, setIsOpen, isCollapsed, setIsCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [isMobile, setIsMobile] = useState(false);

  // Verificar se usuário é admin
  const isAdmin = currentUser?.is_admin === true;

  // Detectar mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth < 1024) {
        setIsCollapsed(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [setIsCollapsed]);

  // Fechar sidebar ao navegar no mobile
  useEffect(() => {
    if (isMobile) {
      setIsOpen(false);
    }
  }, [location.pathname, isMobile, setIsOpen]);

  // Handler de logout
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { 
      icon: LayoutDashboard, 
      label: 'Workspace', 
      path: '/contents',
      badge: null,
      comingSoon: false
    },
    { 
      icon: Calendar, 
      label: 'Calendário', 
      path: '/calendar',
      badge: null,
      comingSoon: false
    },
    {
      icon: Globe,
      label: 'Redes Sociais',
      path: '/social',
      badge: null,
      comingSoon: false
    },
    {
      icon: Briefcase,
      label: 'Serviços',
      path: '/services',
      badge: 'Novo',
      comingSoon: false
    },
  ];

  const bottomNavItems = [
    { 
      icon: User, 
      label: 'Minha Conta', 
      path: '/account',
      comingSoon: false
    },
    { 
      icon: Info, 
      label: 'Sobre', 
      path: '/about',
      comingSoon: false
    },
  ];

  // Itens de admin (só aparecem para admins)
  const adminNavItem = {
    icon: Shield,
    label: 'Admin Config',
    path: '/admin/config',
    badge: 'Admin',
    badgeColor: 'bg-amber-100 text-amber-700',
    comingSoon: false
  };

  const labNavItem = {
    icon: FlaskConical,
    label: 'Lab de Posts',
    path: '/admin/lab',
    badge: 'Lab',
    badgeColor: 'bg-purple-100 text-purple-700',
    comingSoon: false
  };

  const adminServicesNavItem = {
    icon: LifeBuoy,
    label: 'Tickets',
    path: '/admin/services',
    badge: 'Admin',
    badgeColor: 'bg-amber-100 text-amber-700',
    comingSoon: false
  };

  const isActive = (path) => {
    if (path === '/contents') {
      return location.pathname === '/contents' || 
             location.pathname.startsWith('/contents/batch');
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Conteúdo da Sidebar
  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-white">
      {/* Logo - apenas a logo diminui ao minimizar */}
      <div className={`flex items-center justify-center px-5 h-16 lg:h-20 border-b border-gray-200`}>
        <Link to="/" className="flex items-center justify-center w-full">
          {isCollapsed && !isMobile ? (
            <img 
              src="/logo.png" 
              alt="Logo" 
              className="h-6 w-auto transition-all duration-300"
            />
          ) : (
            <img 
              src="/logo.png" 
              alt="Logo" 
              className="h-12 w-auto max-w-[180px] transition-all duration-300"
            />
          )}
        </Link>
      </div>

      {/* Botão Criar Novo */}
      <div className="px-4 py-5">
        <Link
          to="/contents/new"
          className={`flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98] ${
            isCollapsed && !isMobile ? 'px-0' : 'px-4'
          }`}
        >
          <Plus className="w-5 h-5 flex-shrink-0" />
          {(!isCollapsed || isMobile) && <span>Novo Projeto</span>}
        </Link>
      </div>

      {/* Navegação Principal */}
      <nav className="flex-1 px-4 py-3 overflow-y-auto">
        <div className="space-y-1.5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.comingSoon ? '#' : item.path}
                onClick={(e) => item.comingSoon && e.preventDefault()}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all group ${
                  item.comingSoon
                    ? 'text-gray-400 cursor-not-allowed'
                    : active
                    ? 'bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                } ${isCollapsed && !isMobile ? 'justify-center px-3' : ''}`}
                title={isCollapsed && !isMobile ? item.label : undefined}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${
                  item.comingSoon
                    ? 'text-gray-300'
                    : active 
                    ? 'text-amber-600' 
                    : 'text-gray-400 group-hover:text-gray-600'
                }`} />
                {(!isCollapsed || isMobile) && (
                  <>
                    <span className="font-medium flex-1">{item.label}</span>
                    {item.badge && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-400 text-xs font-medium rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </div>

        {/* Separador */}
        {(!isCollapsed || isMobile) && (
          <div className="my-5 px-3">
            <div className="h-px bg-gray-100" />
          </div>
        )}

        {/* ========================================
            MENU ADMIN - Só aparece para is_admin = true
            ======================================== */}
        {isAdmin && (
          <>
            {(!isCollapsed || isMobile) && (
              <div className="mb-2 px-1">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Administração
                </span>
              </div>
            )}
            
            <Link
              to={adminNavItem.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all group ${
                isActive(adminNavItem.path)
                  ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              } ${isCollapsed && !isMobile ? 'justify-center px-3' : ''}`}
              title={isCollapsed && !isMobile ? adminNavItem.label : undefined}
            >
              <adminNavItem.icon className={`w-5 h-5 flex-shrink-0 ${
                isActive(adminNavItem.path) 
                  ? 'text-amber-400' 
                  : 'text-gray-400 group-hover:text-gray-600'
              }`} />
              {(!isCollapsed || isMobile) && (
                <>
                  <span className="font-medium flex-1">{adminNavItem.label}</span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${adminNavItem.badgeColor}`}>
                    {adminNavItem.badge}
                  </span>
                </>
              )}
            </Link>

            <Link
              to={labNavItem.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all group ${
                isActive(labNavItem.path)
                  ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              } ${isCollapsed && !isMobile ? 'justify-center px-3' : ''}`}
              title={isCollapsed && !isMobile ? labNavItem.label : undefined}
            >
              <labNavItem.icon className={`w-5 h-5 flex-shrink-0 ${
                isActive(labNavItem.path)
                  ? 'text-purple-400'
                  : 'text-gray-400 group-hover:text-gray-600'
              }`} />
              {(!isCollapsed || isMobile) && (
                <>
                  <span className="font-medium flex-1">{labNavItem.label}</span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${labNavItem.badgeColor}`}>
                    {labNavItem.badge}
                  </span>
                </>
              )}
            </Link>

            <Link
              to={adminServicesNavItem.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all group ${
                isActive(adminServicesNavItem.path)
                  ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              } ${isCollapsed && !isMobile ? 'justify-center px-3' : ''}`}
              title={isCollapsed && !isMobile ? adminServicesNavItem.label : undefined}
            >
              <adminServicesNavItem.icon className={`w-5 h-5 flex-shrink-0 ${
                isActive(adminServicesNavItem.path)
                  ? 'text-amber-400'
                  : 'text-gray-400 group-hover:text-gray-600'
              }`} />
              {(!isCollapsed || isMobile) && (
                <>
                  <span className="font-medium flex-1">{adminServicesNavItem.label}</span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${adminServicesNavItem.badgeColor}`}>
                    {adminServicesNavItem.badge}
                  </span>
                </>
              )}
            </Link>

            {(!isCollapsed || isMobile) && (
              <div className="my-5 px-3">
                <div className="h-px bg-gray-100" />
              </div>
            )}
          </>
        )}
      </nav>

      {/* Navegação Inferior */}
      <div className="px-4 py-4 border-t border-gray-200">
        <div className="space-y-1.5">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.comingSoon ? '#' : item.path}
                onClick={(e) => item.comingSoon && e.preventDefault()}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  item.comingSoon
                    ? 'text-gray-400 cursor-not-allowed'
                    : active
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                } ${isCollapsed && !isMobile ? 'justify-center px-3' : ''}`}
                title={isCollapsed && !isMobile ? item.label : undefined}
              >
                <Icon className={`w-5 h-5 ${
                  item.comingSoon ? 'text-gray-300' : active ? 'text-gray-700' : 'text-gray-400'
                }`} />
                {(!isCollapsed || isMobile) && (
                  <>
                    <span className="font-medium flex-1">{item.label}</span>
                    {item.badge && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-400 text-xs font-medium rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </div>

        {/* Botão Recolher (apenas desktop) */}
        {!isMobile && (
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="flex items-center justify-center w-full mt-5 py-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-xl transition-all"
          >
            {isCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeft className="w-5 h-5" />
                <span className="ml-2 text-sm font-medium">Recolher</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Botão Menu Mobile */}
      {isMobile && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed top-4 left-4 z-40 p-2.5 bg-white rounded-xl shadow-lg border border-gray-200 lg:hidden"
        >
          <Menu className="w-5 h-5 text-gray-700" />
        </button>
      )}

      {/* Overlay Mobile */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar Mobile */}
      {isMobile && (
        <aside
          className={`fixed top-0 left-0 h-full w-72 bg-white z-50 shadow-2xl transform transition-transform duration-300 ease-out lg:hidden ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {/* Botão Fechar Mobile */}
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-5 right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
          <SidebarContent />
        </aside>
      )}

      {/* Sidebar Desktop */}
      {!isMobile && (
        <aside
          className={`fixed top-0 left-0 h-full bg-white border-r border-gray-200 z-30 transition-all duration-300 ease-out ${
            isCollapsed ? 'w-20' : 'w-64'
          }`}
        >
          <SidebarContent />
        </aside>
      )}
    </>
  );
};

export default Sidebar;